/**
 * @module  js/stores/contactsstore.js
 * @class   ContactsStore
 * @summary Reactive, in-memory cache for CRM contacts with deals and companies.
 *
 * @description
 * Aggregates contact data from the CRM, flattens associations, and exposes it
 * through an event-driven API. Provides bulk-create and polling helpers for
 * LinkedIn import and CRM propagation workflows.
 *
 * ## Key Features
 * – Caches contacts, their deals, and companies with per-account indexing
 * – Bulk-create helper seeds optimistic cache for LinkedIn imports
 * – Polling utility waits for CRM updates with back-off logic
 * – Lightweight pub/sub for controllers to re-render on change
 *
 * ## Public API
 * – `loadContactsForAccount(accountId, {force})` — fetch contacts for an account
 * – `getContact(contactId, {refresh})`` — retrieve single contact with relations
 * – `bulkCreate(rawContacts)` — create many contacts and return results
 * – `poll(contactId, predicateFn, options)` — await predicate truthy or timeout
 * – `on(event, cb)` / `off(event, cb)` — subscribe or unsubscribe to changes
 *
 * ## Dependencies
 * { ApiClient, AuthService, CRMService, Endpoints }
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

export default class ContactsStore {
  /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  #apiClient;
  #authService;
  #crmService;
  #endpoints;

  // In-memory caches
  // contactId → { contact, deals, companies }
  #contactCache = new Map();

  // accountId → [contactIds …]    (simple index for quick lookup)
  #accountIndex = new Map();

  // Pub/Sub listeners
  #listeners = { change: new Set() };

  constructor({ ApiClient, AuthService, CRMService, Endpoints }) {
    this.#apiClient = ApiClient;
    this.#authService = AuthService;
    this.#crmService = CRMService;
    this.#endpoints = Endpoints;
  }

  /* ====================================================================
   * LOADERS
   * ==================================================================== */
  /**
   * Fetch every contact for an account (company) once.
   * Keeps an index for AccountPageController without re-hits.
   */
  async loadContactsForAccount(
    accountId,
    { force = false, userId = null, crm = null } = {}
  ) {
    if (!force && this.#accountIndex.has(accountId)) {
      const ids = this.#accountIndex.get(accountId);
      return ids.map((id) => this.#contactCache.get(id).contact);
    }

    const uid = userId ?? (await this.#authService.requireUser()).uid;
    const provider = crm ?? (await this.#crmService.getCurrentCRM(uid));
    const url = this.#crmService.buildObjectEndpoint(
      'contacts',
      'GET',
      { userId: uid, accountId },
      provider
    );
    const resp = await this.#apiClient.fetch(url, { method: 'GET' });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(
        err.error || `Failed to fetch contacts for account ${accountId}`
      );
    }

    const { contacts = [] } = await resp.json();
    const ids = [];

    contacts.forEach((c) => {
      ids.push(c.id);
      this.#contactCache.set(c.id, { contact: c }); // deals/companies lazy-filled
    });

    this.#accountIndex.set(accountId, ids);
    this.#emit('change');
    return contacts;
  }

  /**
   * Get a single contact (+ optional associations) from cache or CRM.
   * @param {string}  contactId
   * @param {Object}  options
   * @param {boolean} options.refresh – bypass cache
   */
  async getContact(contactId, { refresh = false } = {}) {
    if (!refresh && this.#contactCache.has(contactId)) {
      return this.#contactCache.get(contactId);
    }

    const user = await this.#authService.requireUser();
    const crm = await this.#crmService.getCurrentCRM(user.uid);
    const url = this.#crmService.buildObjectEndpoint(
      'contacts',
      'GET',
      { userId: user.uid, id: contactId },
      crm
    );
    const resp = await this.#apiClient.fetch(url, { method: 'GET' });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || `Failed to fetch contact ${contactId}`);
    }

    const { contact, deals = [], companies = [] } = await resp.json();
    this.#contactCache.set(contactId, { contact, deals, companies });
    this.#emit('change');
    return this.#contactCache.get(contactId);
  }

  /**
   * Fetch contacts for a company on behalf of an arbitrary user.
   * Used by admin impersonation tools.
   *
   * @param {string} accountId
   * @param {{ userId?:string, crm?:string }} [context]
   * @param {boolean} [force=false]
   */
  async getContactsForCompany(accountId, context = {}, force = false) {
    return this.loadContactsForAccount(accountId, {
      force,
      userId: context.userId,
      crm: context.crm,
    });
  }

  /**
   * Clear all cached contacts (e.g. when switching users).
   */
  clearCache() {
    this.#contactCache.clear();
    this.#accountIndex.clear();
  }

  /* ====================================================================
   * BULK-CREATE  (used by LinkedInScrapeService)
   * ==================================================================== */
  /**
   * Create contacts in bulk (array of raw property maps) through backend CF.
   * Returns array of { contactId, status } results.
   * @param {Array<Object>} rawContacts
   */
  async bulkCreate(rawContacts = []) {
    if (!Array.isArray(rawContacts) || !rawContacts.length) return [];

    const user = await this.#authService.requireUser();
    const crm = await this.#crmService.getCurrentCRM(user.uid);
    const url = this.#endpoints.bulkCreateContacts({ userId: user.uid, crm });
    const resp = await this.#apiClient.fetch(url, {
      method: 'POST',
      body: JSON.stringify({ userId: user.uid, contacts: rawContacts, crm }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || 'Bulk-create contacts failed');
    }

    const { results = [] } = await resp.json();
    // Immediately seed cache with skeletons so UI can show optimistic list
    results.forEach(({ contactId, properties = {} }) => {
      this.#contactCache.set(contactId, {
        contact: { id: contactId, properties },
      });
    });

    this.#emit('change');
    return results;
  }

  /* ====================================================================
   * POLLING (e.g. wait until newly-created contact appears in CRM)
   * ==================================================================== */
  /**
   * Poll CRM until predicate(contactData) returns truthy OR maxAttempts hit.
   * Resolves boolean indicating success.
   */
  async poll(
    contactId,
    predicateFn,
    { intervalMs = 1500, maxAttempts = 10 } = {}
  ) {
    for (let i = 0; i < maxAttempts; i++) {
      const data = await this.getContact(contactId, { refresh: true });
      if (predicateFn(data)) return true;
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return false;
  }

  /* ====================================================================
   * PUB / SUB
   * ==================================================================== */
  on(event, cb) {
    this.#listeners[event]?.add(cb);
  }
  off(event, cb) {
    this.#listeners[event]?.delete(cb);
  }

  #emit(evt) {
    this.#listeners[evt]?.forEach((cb) => {
      try {
        cb();
      } catch {}
    });
  }
}
